import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { UserCard } from "@/components/user-card/UserCard.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { EmployeeRecognitionGiftModal } from "@/pages/employees/employee/widgets/EmployeeRecognitionGiftModal.tsx";
import { EmployeeRecognitionModal } from "@/pages/employees/employee/widgets/EmployeeRecognitionModal.tsx";
import { useEmployeeRecognitionsSearch } from "@/queries/employees/use-employee-recognitions.query.ts";
import { ApiEmployeeRecognitionFilter } from "@/types/recognitions/recognition.types.ts";
import {
  Box,
  Button,
  EmptyStateBase,
  PaginationBlock,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const defaultVisibleCount = 8;

export function EmployeeRecognitions({ employee }: EmployeePageComponentProps) {
  const { t } = useTranslation();
  const { renderEmployeeName, renderDate } = useFormatter();
  const { openModal } = useModals();

  const [isShowAll, setIsShowAll] = useState(false);
  const [filter, setFilter] = useState<ApiEmployeeRecognitionFilter>({ page_number: 1 });

  const { data, isLoading } = useEmployeeRecognitionsSearch(employee.id, filter);

  const items = data?.items ?? [];
  const count = data?.metadata.total_items ?? 0;
  const canGift = data?.can_gift ?? false;

  const visibleItems = isShowAll ? items : items.slice(0, defaultVisibleCount);
  const remainingCount = Math.max(count - defaultVisibleCount, 0);
  const shouldShowMore = !isLoading && items.length > 0 && !isShowAll && remainingCount > 0;

  return (
    <Stack gap={"lg"}>
      <Stack direction={"horizontal"} items={"center"} justify={"between"} gap={"md"}>
        <Title
          size={"lg"}
          header={t("recognition:profile_title")}
          caption={t("recognition:profile_count", { count })}
        />
        {canGift && (
          <Button
            variant={"secondary"}
            onClick={() => openModal(EmployeeRecognitionGiftModal, { employee })}
          >
            {t("recognition:profile_gift")}
          </Button>
        )}
      </Stack>

      {isLoading ? (
        <Skeleton className={"h-40 w-full"} />
      ) : items.length === 0 ? (
        <EmptyStateBase
          title={t("recognition:profile_empty_title")}
          description={t("recognition:profile_empty_description")}
        />
      ) : (
        <>
          <Box
            className={"grid gap-(--gap-md)"}
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))" }}
          >
            {visibleItems.map((item) => (
              <UserCard
                key={item.id}
                size={"md"}
                className={"w-full"}
                onClick={() => openModal(EmployeeRecognitionModal, { employee, recognition: item })}
                avatarElement={<RecognitionBadge template={item.recognition} />}
                title={item.recognition_title}
                caption={t("recognition:by_author", {
                  name: renderEmployeeName(item.awarded_by),
                })}
                extra={renderDate(item.datetime_awarded_at)}
              />
            ))}
          </Box>

          {shouldShowMore && (
            <Stack className={"pt-(--gap-md)"} direction={"horizontal"}>
              <Button variant={"secondary"} onClick={() => setIsShowAll(true)}>
                {t("common:show_more_template", { count: remainingCount })}
              </Button>
            </Stack>
          )}

          {isShowAll && data && data.metadata.total_pages > 1 && (
            <PaginationBlock
              metadata={data.metadata}
              onPageChange={(page) => setFilter((prev) => ({ ...prev, page_number: page }))}
            />
          )}
        </>
      )}
    </Stack>
  );
}
